import { Editor } from '@tinymce/tinymce-react';

import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import ArticlesApi from '../../../Api/CRM/Modules/ArticlesApi';
import Article from '../../../Api/Models/Article';
import TrueFalseIcon from '../../../components/common/TrueFalseIcon';
import useGlobalState from '../../GlobalStateContext';
import Card from '../../elements/common/Card';
import Row, { TableDetailsBody } from '../../elements/common/TableDetailsRow';
import useNumberParams from '../../hooks/useNumberParams';
import useOnce from '../../hooks/useOnce';

import ArticleSlugLink from './ArticleSlugLink';

import useEffectWhen from 'base/hooks/useEffectWhen';
import SweetAlert from 'crm/elements/alerts/SweetAlert';
import Select, { KeyValuePairs, SelectOptions } from 'crm/elements/formElements/Select';
import { convertUtcToUserTimezone } from 'utils/helpers';

async function handleLanguageChange(
    isPartial: boolean,
    translationId: number,
    languageId: number,
    title: string,
    slug: string,
    isBlog: boolean,
    isCasinoPage: boolean,
    noDeletable: boolean,
    noIndex: boolean
) {
    const translation = await ArticlesApi.getByTranslationIdAndLanguageId(translationId, languageId);
    if (!translation.id) {
        SweetAlert.warning('Translation does not exist.', 'Create a new translation').then((result) => {
            if (result === true) {
                window.location.href = `/crea/superadmin/articles/create${
                    isPartial ? '/partial' : ''
                }?translation_id=${translationId}&language_id=${languageId}&title=${encodeURIComponent(
                    title
                )}&slug=${encodeURIComponent(slug)}&is_blog=${Number(isBlog)}&is_casino_page=${Number(
                    isCasinoPage
                )}&no_deletable=${Number(noDeletable)}&no_index=${Number(noIndex)}`;
            }
        });

        return;
    }

    window.location.href = `/crea/superadmin/articles/${translation.id}`;
}

function SingleArticle() {
    const { id } = useNumberParams();
    const [article, setArticle] = useState<Article>({} as Article);
    const { languageNames } = useGlobalState();
    const [translations, setTranslations] = useState<SelectOptions>([]);

    useOnce(() => ArticlesApi.getById(id).then(setArticle));
    useEffectWhen(article.id > 0, () => ArticlesApi.getTranslations(article.is_partial).then(setTranslations));

    return (
        <Card title="Article details">
            <TableDetailsBody>
                <Row label="ID">{article.id}</Row>
                {!article.is_partial && (
                    <>
                        <Row label="Meta title">{article.seo_tag?.title}</Row>
                        <Row label="Meta description">{article.seo_tag?.meta_description}</Row>
                        <Row label="Meta keywords">{article.seo_tag?.meta_keywords}</Row>
                        <Row label="OG description">{article.seo_tag?.og_description}</Row>
                        <Row label="OG image">{article.seo_tag?.og_image}</Row>
                        <Row label="OG url">{article.seo_tag?.og_url}</Row>
                        <Row label="OG type">{article.seo_tag?.og_type}</Row>
                    </>
                )}

                <Row label="Title">{article.title}</Row>
                <Row label="Slug">
                    <ArticleSlugLink article={article} />
                </Row>
                <Row label="Language">{languageNames[article.language_id]}</Row>
                <Row label="Base article of Translation ID">
                    {(translations as KeyValuePairs).find(([key]) => key === article.translation_id)?.[1]}
                </Row>
                <Row label="Translations">
                    <Select
                        options={languageNames}
                        value={article.language_id}
                        label=""
                        required
                        showLabel={false}
                        error={null}
                        handleChange={(e) => {
                            handleLanguageChange(
                                article.is_partial,
                                article.translation_id,
                                Number(e.target.value),
                                article.title,
                                article.slug,
                                article.is_blog,
                                article.is_casino_page,
                                article.no_deletable,
                                article.no_index
                            );
                        }}
                        field="article.language_id"
                    />
                </Row>
                <Row label="Is blog">
                    <TrueFalseIcon status={article.is_blog} />
                </Row>
                <Row label="Is Casino page">
                    <TrueFalseIcon status={article.is_casino_page} />
                </Row>
                <Row label="Text">
                    <Editor id="text" init={{ height: 400 }} value={article.text} disabled />
                </Row>
                <Row label="System Pages Article">
                    <TrueFalseIcon status={article.is_partial} />
                </Row>
                <Row label="No deletable">
                    <TrueFalseIcon status={article.no_deletable} />
                </Row>
                <Row label="No index">
                    <TrueFalseIcon status={article.no_index} />
                </Row>
                <Row label="Publication date">
                    {article.publication_date ? convertUtcToUserTimezone(article.publication_date) : ''}
                </Row>
            </TableDetailsBody>
            <Link to={`/articles/${article.id}/edit`} className="btn btn-default">
                Edit article
            </Link>
        </Card>
    );
}

export default SingleArticle;
